import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import noLogo from "../../../../assets/img/brand/noLogo";
import moment from "moment";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const safeText = (value, fallback = "—") => {
  const text = (value ?? "").toString().trim();
  return text.length ? text : fallback;
};

const toNum = (value) => {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
};

const money = (value) =>
  Math.max(0, Math.round((toNum(value) + Number.EPSILON) * 100) / 100);

const fmtMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(money(value));

const formatDate = (value) => {
  const defaultDate = moment().subtract(1, "month").endOf("month");
  const date = value
    ? moment(value, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601], true)
    : defaultDate;
  return date.isValid()
    ? date.format("DD/MM/YYYY")
    : defaultDate.format("DD/MM/YYYY");
};

const layoutGrid = {
  hLineWidth: () => 0.8,
  vLineWidth: () => 0.8,
  hLineColor: () => "#000",
  vLineColor: () => "#000",
  paddingLeft: () => 6,
  paddingRight: () => 6,
  paddingTop: () => 2,
  paddingBottom: () => 2,
};

const layoutNoBorder = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

const ncfPrincipalReport = (data = {}) => {

  const details = Array.isArray(data?.details) ? data.details : [];
  const normalizedDetails = details.length
    ? details.map((item) => ({
        description: safeText(item?.description, "Servicios de Salud"),
        quantity: money(item?.quantity),
        price: money(item?.price),
        value: money(item?.value),
      }))
    : [
        {
          description: safeText(data?.concept, "Servicios de Salud"),
          quantity: money(data?.quantity || 1),
          price: money(data?.price || data?.value),
          value: money(data?.value),
        },
      ];

  const totalValue = money(
    data?.total ?? normalizedDetails.reduce((acc, item) => acc + item.value, 0),
  );

  const consultoryRncRaw = (data?.consultoryRnc ?? "").toString().trim();
  const insurerRncRaw = (
    data?.insurerRnc ??
    data?.rnc ??
    data?.arsRnc ??
    data?.ars?.rnc ??
    ""
  )
    .toString()
    .trim();

  const consultoryHeaderLines = [
    {
      text: safeText(data?.consultoryName, "Consultorio") + "\n",
      bold: true,
    },
    { text: safeText(data?.consultoryAddress, "Direccion") + "\n" },
  ];
  if (consultoryRncRaw) {
    consultoryHeaderLines.push(`RNC ${consultoryRncRaw}`);
  }

  const logoBlock = () => {
    if (data?.logoDataUrl) {
      return { image: data.logoDataUrl, width: 140 };
    }

    return {
      layout: layoutNoBorder,
      width: 150,
      table: { widths: ["*"], body: [[{ image: noLogo, width: 140 }]] },
    };
  };

  const voucherDueDate = moment(data?.voucherDueDate);
  const voucherDueDateLabel = voucherDueDate.isValid()
    ? voucherDueDate.format("DD/MM/YYYY")
    : "N/A";
  const generatedDateLabel = formatDate(
    data?.generatedAt || data?.dateLabel || data?.date,
  );

  return {
    pageMargins: [30, 30, 30, 20],
    defaultStyle: { fontSize: 10 },
    styles: {
      title: { fontSize: 13, bold: true, decoration: "underline" },
      sectionTitle: { fontSize: 10, bold: true, decoration: "underline" },
      th: { bold: true, fontSize: 10 },
      td: { fontSize: 10 },
    },
    content: [
      {
        columns: [
          {
            width: 170,
            stack: [
              { ...logoBlock() },
              {
                text: consultoryHeaderLines,
                margin: [0, 2, 0, 0],
              },
              {
                margin: [0, 12, 0, 0],
                text: [
                  { text: "Aseguradora: ", bold: true },
                  safeText(data?.insurerName),
                ],
              },
              ...(insurerRncRaw
                ? [{ text: [{ text: "RNC: ", bold: true }, insurerRncRaw] }]
                : []),
            ],
          },
          {
            width: "*",
            margin: [0, 30, 0, 0],
            fontSize: 13,
            stack: [
              {
                text: "Factura Comprobante Fiscal",
                style: "title",
                alignment: "right",
                bold: true,
              },
              {
                text: `Fecha: ${generatedDateLabel}`,
                alignment: "right",
                margin: [0, 8, 0, 0],
                bold: true,
                fontSize: 10,
              },
              {
                text: [
                  { text: "NCF: ", bold: true },
                  { text: safeText(data?.ncf, ""), decoration: "underline" },
                  { text: "\n Valido Hasta: ", bold: true },
                  { text: voucherDueDateLabel, decoration: "underline" },
                ],
                alignment: "right",
                margin: [0, 36, 0, 0],
              },
            ],
          },
        ],
        columnGap: 10,
      },
      {
        text: "Concepto",
        style: "sectionTitle",
        margin: [0, 12, 0, 0],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", 80, 90, 100],
          body: [
            [
              { text: "Concepto", style: "th", alignment: "center" },
              { text: "Cantidad", style: "th", alignment: "center" },
              { text: "Precio", style: "th", alignment: "center" },
              { text: "Valor", style: "th", alignment: "center" },
            ],
            ...normalizedDetails.map((item) => [
              { text: item.description, style: "td" },
              { text: `${item.quantity}`, style: "td", alignment: "right" },
              { text: fmtMoney(item.price), style: "td", alignment: "right" },
              { text: fmtMoney(item.value), style: "td", alignment: "right" },
            ]),
            [
              { text: "", border: [false, false, false, false] },
              { text: "", border: [false, false, false, false] },
              { text: "Total", style: "th", alignment: "center" },
              {
                text: fmtMoney(totalValue),
                style: "th",
                alignment: "right",
              },
            ],
          ],
        },
        layout: layoutGrid,
      },
    ],
  };
};

export const openNcfPrincipalReport = (data = {}) => {
  pdfMake.createPdf(ncfPrincipalReport(data)).open();
};
